import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, LogOut, BookOpen, Sparkles, Lock, Play, Mail, Check, Send, Copy, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

/**
 * MensGuideDashboard — the gated course landing page for "A Man's Guide to
 * Her Menopause" customers. Mirrors the TMI dashboard layout (left sidebar
 * with module nav, main panel with welcome + progress). Members see all six
 * modules; non-members see an upgrade card.
 */
export default function MensGuideDashboard() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [modules, setModules] = useState([]);
  const [answers, setAnswers] = useState({});
  const [reminderLoading, setReminderLoading] = useState(false);
  const hasAccess =
    user && user !== false && (user.has_mens_guide_access || user.role === "admin");
  const optedIn = !!(user && user.mens_guide_weekly_reminder_optin);

  const handleReminderOptIn = async () => {
    setReminderLoading(true);
    try {
      await api.put("/mens-guide/reminders", { opt_in: true });
      await refreshUser();
      toast.success("Lovely — I'll send you a quiet nudge each week.");
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't save just now — please try again."
      );
    } finally {
      setReminderLoading(false);
    }
  };

  useEffect(() => {
    if (hasAccess) {
      api.get("/mens-guide/modules").then((r) => setModules(r.data || []));
      api.get("/mens-guide/answers").then((r) => setAnswers(r.data || {}));
    } else {
      // Show module previews even if locked
      api.get("/mens-guide/preview").then((r) => setModules(r.data?.modules || []));
    }
  }, [hasAccess]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  // Count reflections answered across all modules
  let totalQuestions = 0;
  let answeredCount = 0;
  if (hasAccess) {
    for (const m of modules) {
      const qs = m.reflection?.questions || [];
      totalQuestions += qs.length;
      const moduleAnswers = answers[m.id] || {};
      for (const q of qs) {
        const text = (moduleAnswers[q.id] || "").trim();
        if (text.length > 0) answeredCount += 1;
      }
    }
  }

  return (
    <div
      className="min-h-screen bg-cream flex"
      data-testid="page-mens-guide-dashboard"
    >
      <aside className="hidden lg:flex w-72 bg-cream-soft border-r border-hairline flex-col">
        <div className="p-8 border-b border-hairline">
          <Link to="/" className="font-serif-display text-2xl text-ink">
            Pip<span className="text-gold italic"> Bennett</span>
          </Link>
          <div className="mt-6">
            <div className="eyebrow text-ink-soft">Signed in as</div>
            <div
              className="mt-1 text-ink font-medium truncate"
              data-testid="mg-sidebar-user"
            >
              {user?.name || user?.email}
            </div>
          </div>
        </div>
        <nav className="flex-1 p-6 space-y-2">
          <div className="eyebrow text-ink-soft mb-4 px-3">
            A Man's Guide to Her Menopause
          </div>
          <Link
            to="/dashboard/mens-guide"
            className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-sage/10 text-sage font-medium"
            data-testid="mg-sidebar-overview"
          >
            <Sparkles size={16} strokeWidth={1.5} /> Overview
          </Link>
          {modules.map((m) => (
            <Link
              key={m.id}
              to={
                hasAccess
                  ? `/dashboard/mens-guide/module/${m.id}`
                  : "/a-mans-guide-to-her-menopause"
              }
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-ink-soft hover:bg-cream hover:text-ink transition text-sm"
              data-testid={`mg-sidebar-module-${m.id}`}
            >
              {hasAccess ? (
                <BookOpen size={14} strokeWidth={1.5} />
              ) : (
                <Lock size={14} strokeWidth={1.5} />
              )}
              <span className="truncate">
                Module {m.number} · {m.title}
              </span>
            </Link>
          ))}
        </nav>
        <div className="p-6 border-t border-hairline">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 text-ink-soft hover:text-ink text-sm w-full"
            data-testid="mg-sidebar-logout"
          >
            <LogOut size={14} strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <div className="px-6 md:px-10 lg:px-16 py-10 md:py-14 max-w-5xl">
          <div className="lg:hidden flex items-center justify-between mb-10">
            <Link to="/" className="font-serif-display text-xl text-ink">
              Pip<span className="text-gold italic"> Bennett</span>
            </Link>
            <button
              onClick={handleLogout}
              className="text-ink-soft text-sm"
              data-testid="mg-mobile-logout"
            >
              Sign out
            </button>
          </div>

          <div className="eyebrow mb-4 text-sage">Welcome</div>
          <h1
            className="font-serif-display text-4xl md:text-6xl text-ink leading-[1.05]"
            data-testid="mg-dashboard-title"
          >
            A Man's Guide to <em className="italic text-sage">Her Menopause</em>.
          </h1>
          <p className="mt-6 text-ink-soft max-w-xl leading-relaxed">
            {hasAccess
              ? `Welcome, ${user?.name || "and thank you for being here"}. Move at your own pace. Your written reflections save automatically as you go — they'll be here whenever you return.`
              : "You're almost in. Unlock the program to access all six modules with embedded video teachings and your private reflection journal."}
          </p>

          {!hasAccess && (
            <div
              className="mt-10 bg-sage text-cream rounded-2xl p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center gap-6 justify-between"
              data-testid="mg-dashboard-upgrade"
            >
              <div>
                <div className="eyebrow mb-2 text-gold">Unlock</div>
                <h2 className="font-serif-display text-2xl md:text-3xl">
                  A Man's Guide to Her Menopause
                </h2>
                <p className="mt-3 text-cream/75 max-w-md">
                  Lifetime access to all six modules with embedded video teachings and
                  your private reflection journal — for $97 AUD.
                </p>
              </div>
              <Link
                to="/a-mans-guide-to-her-menopause"
                className="inline-flex items-center gap-2 bg-gold hover:bg-gold-dark text-ink rounded-full px-8 py-4 font-medium whitespace-nowrap transition-all"
                data-testid="mg-dashboard-upgrade-cta"
              >
                Begin — $97
                <ArrowRight size={16} strokeWidth={1.8} />
              </Link>
            </div>
          )}

          {hasAccess && totalQuestions > 0 && (
            <div
              className="mt-10 bg-white border border-hairline rounded-2xl p-6 md:p-8 flex items-center gap-6"
              data-testid="mg-progress"
            >
              <div className="font-serif-display text-4xl md:text-5xl text-sage tabular-nums">
                {answeredCount}
                <span className="text-ink-soft text-2xl">/{totalQuestions}</span>
              </div>
              <div className="flex-1">
                <div className="eyebrow text-sage">Reflections answered</div>
                <p className="mt-1 text-ink-soft text-sm">
                  Quiet, considered work. There's no rush — and no right answer.
                </p>
              </div>
            </div>
          )}

          {hasAccess && (
            optedIn ? (
              <div
                className="mt-6 bg-sage/[0.06] border border-sage/25 rounded-2xl p-6 md:p-7 flex items-start gap-4"
                data-testid="mg-reminder-confirmed"
              >
                <span className="flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-full bg-sage text-cream mt-0.5">
                  <Check size={16} strokeWidth={2} />
                </span>
                <div>
                  <div className="eyebrow text-sage mb-1">You'll hear from me</div>
                  <p className="text-ink-soft leading-relaxed text-sm">
                    Three short notes coming your way over the next few weeks — a
                    welcome, a check-in, and a closing reflection. Quiet, considered,
                    and easy to switch off any time.
                  </p>
                </div>
              </div>
            ) : (
              <div
                className="mt-6 bg-cream-soft border border-hairline rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-5 justify-between"
                data-testid="mg-reminder-prompt"
              >
                <div className="flex items-start gap-4">
                  <span className="flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-full bg-sage/10 text-sage mt-0.5">
                    <Mail size={16} strokeWidth={1.7} />
                  </span>
                  <div>
                    <div className="eyebrow text-sage mb-1">A small steadying habit</div>
                    <h3 className="font-serif-display text-xl text-ink leading-snug">
                      Want quiet support from me while you work through this?
                    </h3>
                    <p className="mt-2 text-sm text-ink-soft leading-relaxed max-w-md">
                      Three short notes from me over the next few weeks — a welcome,
                      a check-in, and a closing reflection. No sales talk. Easy to
                      switch off any time.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleReminderOptIn}
                  disabled={reminderLoading}
                  className="inline-flex items-center justify-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-7 py-3.5 font-medium whitespace-nowrap transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                  data-testid="mg-reminder-optin"
                >
                  {reminderLoading ? "Saving…" : "Yes, send them"}
                  {!reminderLoading && <ArrowRight size={14} strokeWidth={1.8} />}
                </button>
              </div>
            )
          )}

          {hasAccess && (
            <MensGuideShareCard
              referralCode={user?.referral_code || ""}
              firstName={(user?.name || "").split(" ")[0] || "your mate"}
            />
          )}

          <h2
            className="mt-14 md:mt-20 font-serif-display text-2xl md:text-3xl text-ink"
            data-testid="mg-modules-heading"
          >
            {hasAccess ? "Your modules" : "What's inside"}
          </h2>
          <div className="mt-8 space-y-4">
            {modules.map((m) => (
              <Link
                key={m.id}
                to={
                  hasAccess
                    ? `/dashboard/mens-guide/module/${m.id}`
                    : "/a-mans-guide-to-her-menopause"
                }
                className="block group bg-white border border-hairline hover:border-sage rounded-2xl p-6 md:p-7 transition"
                data-testid={`mg-module-card-${m.id}`}
              >
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 rounded-full bg-sage/10 text-sage inline-flex items-center justify-center flex-shrink-0">
                    {hasAccess ? (
                      <Play size={16} strokeWidth={1.6} fill="currentColor" />
                    ) : (
                      <Lock size={16} strokeWidth={1.6} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="eyebrow text-sage">Module {m.number}</div>
                    <h3 className="mt-1 font-serif-display text-xl text-ink leading-snug">
                      {m.title}
                    </h3>
                    <p className="mt-2 text-sm text-ink-soft leading-relaxed">
                      {m.tagline || m.summary}
                    </p>
                  </div>
                  <ArrowRight
                    size={16}
                    strokeWidth={1.6}
                    className="text-ink-soft group-hover:text-sage group-hover:translate-x-0.5 transition flex-shrink-0 mt-2"
                  />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}


function MensGuideShareCard({ referralCode, firstName }) {
  const [copied, setCopied] = useState(false);
  const [logging, setLogging] = useState(false);

  // Build the shareable URL — with the user's referral code if they have one.
  const shareUrl = referralCode
    ? `https://pipbennett.com/a-mans-guide-to-her-menopause?ref=${encodeURIComponent(referralCode)}`
    : "https://pipbennett.com/a-mans-guide-to-her-menopause";

  // Message kept simple and honest — signed off in the user's voice rather
  // than ours, so it feels like a real mate-to-mate recommendation.
  const shareMessage = `Mate — this program from Pip Bennett helped me make sense of what my partner's going through (and what I was getting wrong). Six short modules. $97 AUD, one-time, you keep it forever. Worth a look:\n\n${shareUrl}`;

  // Quietly log which channel was used so Pip can see what's working in admin.
  const logShare = (channel) => {
    setLogging(true);
    api
      .post("/referral/share-event", { channel, program: "mens_guide" })
      .catch(() => {
        /* never break the share flow */
      })
      .finally(() => setLogging(false));
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareMessage);
      setCopied(true);
      logShare("copy");
      setTimeout(() => setCopied(false), 2400);
    } catch {
      toast.error("Couldn't copy — please try again or use one of the share buttons.");
    }
  };

  const handleNativeShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: "A Man's Guide to Her Menopause",
          text: shareMessage,
          url: shareUrl,
        });
        logShare("native");
      } catch {
        /* user cancelled — silent */
      }
    } else {
      handleCopy();
    }
  };

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(shareMessage)}`;
  const emailHref = `mailto:?subject=${encodeURIComponent(
    "A program worth a look",
  )}&body=${encodeURIComponent(shareMessage)}`;
  const smsHref = `sms:?&body=${encodeURIComponent(shareMessage)}`;

  return (
    <div
      className="mt-6 bg-cream-soft border border-hairline rounded-2xl p-6 md:p-8"
      data-testid="mg-share-card"
    >
      <div className="flex items-start gap-4">
        <span className="flex-shrink-0 w-10 h-10 inline-flex items-center justify-center rounded-full bg-gold/15 text-gold-dark mt-0.5">
          <Send size={15} strokeWidth={1.7} />
        </span>
        <div className="flex-1">
          <div className="eyebrow text-sage mb-1">Pass it on</div>
          <h3 className="font-serif-display text-xl md:text-2xl text-ink leading-snug">
            Has this been useful?{" "}
            <em className="italic text-sage">Send it to a mate.</em>
          </h3>
          <p className="mt-3 text-sm text-ink-soft leading-relaxed max-w-xl">
            If this program has made a difference for you, the most generous
            thing you can do is quietly send it to one bloke who might be where
            you were. We've drafted a short message — copy it as-is or change a
            line to make it yours.
          </p>
        </div>
      </div>

      <div
        className="mt-5 bg-cream border border-hairline rounded-xl p-4 text-sm text-ink leading-relaxed whitespace-pre-line"
        data-testid="mg-share-message"
      >
        {shareMessage}
      </div>

      <div className="mt-5 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={handleCopy}
          disabled={logging}
          className="inline-flex items-center gap-2 bg-sage hover:bg-sage-dark text-cream rounded-full px-5 py-2.5 text-sm font-medium transition disabled:opacity-60"
          data-testid="mg-share-copy"
        >
          {copied ? (
            <>
              <Check size={14} strokeWidth={2} /> Copied
            </>
          ) : (
            <>
              <Copy size={14} strokeWidth={1.8} /> Copy message
            </>
          )}
        </button>

        <a
          href={whatsappHref}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logShare("whatsapp")}
          className="inline-flex items-center gap-2 bg-cream border border-hairline hover:border-sage text-ink hover:text-sage rounded-full px-5 py-2.5 text-sm font-medium transition"
          data-testid="mg-share-whatsapp"
        >
          <MessageCircle size={14} strokeWidth={1.8} /> WhatsApp
        </a>

        <a
          href={smsHref}
          onClick={() => logShare("sms")}
          className="inline-flex items-center gap-2 bg-cream border border-hairline hover:border-sage text-ink hover:text-sage rounded-full px-5 py-2.5 text-sm font-medium transition md:hidden"
          data-testid="mg-share-sms"
        >
          <MessageCircle size={14} strokeWidth={1.8} /> SMS
        </a>

        <a
          href={emailHref}
          onClick={() => logShare("email")}
          className="inline-flex items-center gap-2 bg-cream border border-hairline hover:border-sage text-ink hover:text-sage rounded-full px-5 py-2.5 text-sm font-medium transition"
          data-testid="mg-share-email"
        >
          <Mail size={14} strokeWidth={1.8} /> Email
        </a>

        {typeof navigator !== "undefined" && navigator.share && (
          <button
            type="button"
            onClick={handleNativeShare}
            className="inline-flex items-center gap-2 bg-cream border border-hairline hover:border-sage text-ink hover:text-sage rounded-full px-5 py-2.5 text-sm font-medium transition md:hidden"
            data-testid="mg-share-native"
          >
            <Send size={14} strokeWidth={1.8} /> Share…
          </button>
        )}
      </div>

      {referralCode && (
        <p
          className="mt-4 text-[11px] text-ink-soft/70 leading-relaxed"
          data-testid="mg-share-referral-note"
        >
          Your link includes your personal referral code{" "}
          <span className="text-ink font-medium tracking-wider">
            {referralCode}
          </span>{" "}
          — if your mate enrols through it, you'll receive a small thank-you
          credit toward future work with Pip.
        </p>
      )}
    </div>
  );
}
